import { useState } from "react";
import { useFormik } from "formik";
import { format, differenceInCalendarMonths } from "date-fns";
import DatePicker from "react-datepicker";
import IconButton from "components/Button/IconButton";
import { ReactComponent as RemoveIcon } from "assets/remove.svg";
import { ReactComponent as CalendarIcon } from "assets/calendar_month.svg";
import { useRef } from "react";
import { useEffect } from "react";
import { useKPI } from "providers/KPIProvider";
import { addMonths } from "../../../../node_modules/date-fns/addMonths";
import DateInput from "components/Input/DateInput";
import { extractNumbers } from "utils/app.util";
import { numberFormatter, parseNumberString } from "utils/number.util";
import { forwardRef } from "react";
import { useImperativeHandle } from "react";
import { KPISchema } from "../KPIMetric/KPIMetric.schema";
import RemoveConfirmation from "./RemoveConfirmation";
import arrow_down from "../../../assets/keyboard_arrow_down.svg";

const defaultKPI = {
  _id: 0,
  name: "",
  target: "",
  startDate: null,
  endDate: null,
  nodeId: null,
};

const KPIForm = forwardRef(
  (
    {
      kpi,
      nodeId,
      updateKPIFormState,
      deleteKPIRow,
      setKPIs,
      kpiLength,
      setSubmitLoading,
    },
    ref
  ) => {
    const submitRef = useRef(null);
    const [duration, setDuration] = useState("3");
    const { addKPI, editKPI, deleteKPI } = useKPI();
    const [removeConfirmation, setRemoveConfirmation] = useState(false);
    const [showDropdown, setShowDropDown] = useState(false);
    const { setValues, setFieldValue, ...formik } = useFormik({
      initialValues: defaultKPI,
      validationSchema: KPISchema,
      onSubmit: async (values) => {
        const { _id, ...data } = values;
        setSubmitLoading(true);

        data.target = Number.parseInt(parseNumberString(data.target));

        if (typeof _id === "string") {
          editKPI(_id, data)
            .then(() => {
              // setSubmitLoading(true);
              updateKPIFormState(values._id);
            })
            .catch((ex) => {
              console.log(ex);
            });
        } else {
          addKPI(data)
            .then(() => {
              // setSubmitLoading(true);
              updateKPIFormState(values._id);
            })
            .catch((ex) => {
              console.log(ex);
            });
        }
      },
    });

    function handleDelete(id) {
      if (typeof id === "string") {
        deleteKPI(id)
          .then(() => deleteKPIRow(id))
          .catch((ex) => {
            console.log(ex);
          });
        setRemoveConfirmation(false);
      } else {
        deleteKPIRow(id);
      }
    }

    useImperativeHandle(
      ref,
      () => ({
        submitForm() {
          submitRef.current.click();
        },
      }),
      []
    );

    useEffect(() => {
      setValues({
        _id: kpi._id,
        name: kpi.name,
        target: kpi.target ? numberFormatter.format(kpi.target) : kpi.target,
        startDate: kpi.startDate,
        endDate: kpi.endDate,
        nodeId: kpi.nodeId || nodeId,
      });

      if (kpi.startDate && kpi.endDate) {
        const numberOfMonths = differenceInCalendarMonths(
          kpi.endDate,
          kpi.startDate
        );
        setDuration(`${numberOfMonths}`);
      }
    }, [nodeId, setValues]);

    useEffect(() => {
      if (duration && formik.values.startDate) {
        const startDate = formik.values.startDate;
        const endDate = addMonths(startDate, Number.parseInt(duration));
        setFieldValue("endDate", endDate);
      }
    }, [formik.values.startDate, setFieldValue, duration, kpi]);

    useEffect(() => {
      const handleClickOutside = () => setShowDropDown(null);
      document.addEventListener("click", handleClickOutside);
      return () => {
        document.removeEventListener("click", handleClickOutside);
      };
    }, []);

    return (
      <form onSubmit={formik.handleSubmit}>
        <div className="grid grid-flow-col auto-cols-fr px-5 py-3 gap-x-4 gap-y-3 font-semibold text-xs">
          <div className="">
            <input
              id="name"
              name="name"
              type="text"
              placeholder="Enter Name"
              value={formik.values.name}
              onChange={(e) => {
                formik.handleChange(e);
                setKPIs((prevKPIs) =>
                  prevKPIs.map((data, i) => {
                    return data._id === kpi._id
                      ? { ...data, name: e.target.value }
                      : data;
                  })
                );
              }}
              onBlur={formik.handleBlur}
            />
            {kpiLength === 1 && formik.touched.name && formik.errors.name && (
              <p className="text-red-400 text-xs font-normal">
                Name is required
              </p>
            )}
          </div>
          <div className="">
            <input
              id="target"
              name="target"
              type="text"
              placeholder="Enter Target"
              value={formik.values.target}
              onChange={(e) => {
                let extractedNumber = extractNumbers(e.target?.value); // Extract digits from input
                let number = extractedNumber
                  ? numberFormatter.format(Number(extractedNumber))
                  : "";
                setFieldValue("target", number);
                setKPIs((prevKPIs) =>
                  prevKPIs.map((data, i) => {
                    return data._id === kpi._id
                      ? {
                          ...data,
                          target: extractedNumber,
                        }
                      : data;
                  })
                );
              }}
              // onChange={(e) =>
              //   setFieldValue(
              //     "target",
              //     numberFormatter.format(extractNumbers(e.target?.value))
              //   )
              // }
              onBlur={formik.handleBlur}
            />
            {kpiLength === 1 &&
              formik.touched.target &&
              formik.errors.target && (
                <p className="text-red-400 text-xs font-normal">
                  Target is required
                </p>
              )}
          </div>
          <div>
            <DatePicker
              className="w-full"
              dateFormat="MM/dd/yyyy"
              placeholderText="mm/dd/yyyy"
              selected={formik.values.startDate || ""}
              disabled={kpi?.metricRecords?.length > 0}
              minDate={new Date()}
              onChange={(date) => {
                setFieldValue("startDate", date);
                setKPIs((prevKPIs) =>
                  prevKPIs.map((data, i) => {
                    return data._id === kpi._id
                      ? {
                          ...data,
                          startDate: date,
                        }
                      : data;
                  })
                );
              }}
              customInput={<DateInput calendarIcon={<CalendarIcon />} />}
            />
            {kpiLength === 1 &&
              formik.touched.startDate &&
              formik.errors.startDate && (
                <p className="text-red-400 text-xs font-normal">
                  Start date is required
                </p>
              )}
          </div>
          <div className="relative">
            <div
              className="dropdown flex justify-between items-center py-[6px] px-[11px] rounded-[6px] w-full border border-gray-300 cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                setShowDropDown(!showDropdown);
              }}
            >
              <span className="text-[12px] font-normal">
                {duration === "12" ? "Yearly" : `${duration} Months`}
              </span>
              <img src={arrow_down} alt="" className="w-2.5" />
            </div>
            {showDropdown && (
              <div className="absolute w-full text-left border border-[#D9D9D9] bg-white rounded-[6px] z-20 flex flex-col">
                <div
                  onClick={() => setDuration("3")}
                  className="dropdown-btn text-[12px] font-medium py-[6px] px-[11px] border-b border-[#D9D9D9] text-left cursor-pointer"
                >
                  3 Months
                </div>
                <div
                  onClick={() => setDuration("6")}
                  className="dropdown-btn text-[12px] font-medium py-[6px] px-[11px] border-b border-[#D9D9D9] text-left cursor-pointer"
                >
                  6 Months
                </div>
                <div
                  onClick={() => setDuration("9")}
                  className="dropdown-btn text-[12px] font-medium py-[6px] px-[11px] border-b border-[#D9D9D9] text-left cursor-pointer"
                >
                  9 Months
                </div>
                <div
                  onClick={() => setDuration("12")}
                  className="dropdown-btn text-[12px] font-medium py-[6px] px-[11px] text-left cursor-pointer"
                >
                  Yearly
                </div>
              </div>
            )}
          </div>
          {/* <select
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            className="h-fit"
          >
            <option value="3">3 Months</option>
            <option value="6">6 Months</option>
            <option value="9">9 Months</option>
            <option value="12">Yearly</option>
          </select> */}
          <input
            type="text"
            placeholder="mm/dd/yyyy"
            value={
              formik.values.endDate
                ? format(formik.values.endDate, "MM/dd/yyyy")
                : ""
            }
            className="h-fit"
            readOnly
          />
          <IconButton
            icon={<RemoveIcon />}
            className="remove"
            onClick={() => {
              console.log(typeof kpi._id);
              if (typeof kpi._id === "number") {
                handleDelete(kpi._id);
              } else {
                setRemoveConfirmation(true);
              }
            }}
          />
          {removeConfirmation && (
            <RemoveConfirmation
              onCancel={setRemoveConfirmation}
              onConfirm={handleDelete}
              kpi={kpi}
            />
          )}
        </div>
        <button type="submit" ref={submitRef} className="hidden"></button>
      </form>
    );
  }
);

export default KPIForm;
